// The reporting period of /auth/reports: inclusive "YYYY-MM" calendar months. Pure functions shared by the filter, the screens and the tests.
// The current month is always the Saudi reporting month (Asia/Riyadh), never the browser's. Comparisons use month indexes, never formatted strings.
import { addMonths, isMonthKey, monthSpan } from "./analysisWindow";

export const ROLLING_WINDOW_MONTHS = 12;
export const REPORT_MIN_YEAR = 2020;
export const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const RIYADH = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Riyadh", year: "numeric", month: "2-digit" });
const pad = (value) => String(value).padStart(2, "0");

export const monthKey = (year, month) => `${year}-${pad(month)}`;
export const compareMonths = (a, b) => monthSpan(b, a) - 1; // < 0 when a is before b

export function currentReportMonth(now = new Date()) {
  const parts = Object.fromEntries(RIYADH.formatToParts(now).map((part) => [part.type, part.value]));
  return monthKey(parts.year, parts.month);
}

export const isFutureMonth = (key, now = new Date()) => compareMonths(key, currentReportMonth(now)) > 0;

// The rolling default: 12 calendar months ending with the current one; it advances by itself when a new month begins.
export function rollingWindow(now = new Date()) {
  const to = currentReportMonth(now);
  return { from: addMonths(to, -(ROLLING_WINDOW_MONTHS - 1)), to };
}

// Changing one end moves the other when the pair would otherwise be invalid, so an invalid pair cannot exist.
export const withFrom = (range, from) => ({ from, to: compareMonths(from, range.to) > 0 ? from : range.to });
export const withTo = (range, to) => ({ to, from: compareMonths(to, range.from) < 0 ? to : range.from });

// The applied range for what the URL holds. No range (or a damaged one) = the rolling window; a future end comes back to the current month.
export function resolveRange({ from, to } = {}, now = new Date()) {
  if (!isMonthKey(from) || !isMonthKey(to)) return { ...rollingWindow(now), isDefault: true };
  const current = currentReportMonth(now);
  const end = compareMonths(to, current) > 0 ? current : to;
  return { from: compareMonths(from, end) > 0 ? end : from, to: end, isDefault: false };
}

// "September 2026" in the given month labels (translations[lang].filter.month).
export const formatMonth = (key, labels = {}) => {
  if (!isMonthKey(key)) return "";
  const [year, month] = key.split("-");
  const name = MONTH_NAMES[Number(month) - 1];
  return `${labels[name] ?? name} ${year}`;
};
